import { useState, useEffect } from 'react';
import { get, post, put, del } from 'aws-amplify/api';
import { CompanyProfile } from '../../types';

// SVG Icon Components for Edit and Delete actions
const EditIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#5934C4'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'></path>
    <path d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'></path>
  </svg>
);

const DeleteIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='20'
    height='20'
    viewBox='0 0 24 24'
    fill='none'
    stroke='#E53E3E'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='3 6 5 6 21 6'></polyline>
    <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'></path>
    <line x1='10' y1='11' x2='10' y2='17'></line>
    <line x1='14' y1='11' x2='14' y2='17'></line>
  </svg>
);

interface CompanyManagerProps {
  onCompaniesUpdate: () => void;
}

export function CompanyManager({ onCompaniesUpdate }: CompanyManagerProps) {
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [editingCompany, setEditingCompany] = useState<CompanyProfile | null>(
    null
  );
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState('');

  const fetchCompanies = async () => {
    try {
      const restOperation = get({
        apiName: 'aslanwebsftpapi',
        path: '/companies',
      });
      const { body } = await restOperation.response;
      const companyList = (await body.json()) as unknown as CompanyProfile[];
      setCompanies(companyList);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setMessage('Could not load companies.');
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage('Saving...');
    const companyData = {
      name: (e.currentTarget.elements.namedItem('name') as HTMLInputElement)
        .value,
      baseSftpFolder: (
        e.currentTarget.elements.namedItem('baseSftpFolder') as HTMLInputElement
      ).value,
      logoUrl: (
        e.currentTarget.elements.namedItem('logoUrl') as HTMLInputElement
      ).value,
    };

    try {
      if (isCreating) {
        await post({
          apiName: 'aslanwebsftpapi',
          path: '/companies',
          options: { body: companyData },
        }).response;
        setMessage('Company created successfully.');
      } else if (editingCompany) {
        await put({
          apiName: 'aslanwebsftpapi',
          path: `/companies/${editingCompany.id}`,
          options: { body: companyData },
        }).response;
        setMessage('Company updated successfully.');
      }
      await fetchCompanies();
      onCompaniesUpdate();
      setEditingCompany(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving company:', error);
      setMessage('Failed to save company.');
    }
  };

  const handleDelete = async (companyId: string) => {
    if (
      window.confirm(
        'Are you sure you want to delete this company? This may affect users associated with it.'
      )
    ) {
      try {
        await del({
          apiName: 'aslanwebsftpapi',
          path: `/companies/${companyId}`,
        }).response;
        setMessage('Company deleted.');
        await fetchCompanies();
        onCompaniesUpdate();
      } catch (error) {
        console.error('Error deleting company:', error);
        setMessage(
          'Failed to delete company. It may still be in use by some users.'
        );
      }
    }
  };

  const EditForm = ({
    company,
    onCancel,
  }: {
    company: Partial<CompanyProfile>;
    onCancel: () => void;
  }) => (
    <form
      onSubmit={handleFormSubmit}
      style={{
        background: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        border: '1px solid #ddd',
        margin: '10px 0',
      }}
    >
      <h4>{isCreating ? 'Create New Company' : `Edit ${company.name}`}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <input type='hidden' name='id' defaultValue={company.id} />
        <div>
          <label>
            Company Name:{' '}
            <input
              type='text'
              name='name'
              defaultValue={company.name}
              required
            />
          </label>
        </div>
        <div>
          <label>
            Base SFTP Folder:{' '}
            <input
              type='text'
              name='baseSftpFolder'
              defaultValue={company.baseSftpFolder}
              required
            />
          </label>
        </div>
        <div>
          <label>
            Logo URL (optional):{' '}
            <input type='url' name='logoUrl' defaultValue={company.logoUrl} />
          </label>
        </div>
        <div>
          <button type='submit'>Save</button>
          <button
            type='button'
            onClick={onCancel}
            style={{ marginLeft: '10px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <div>
      <h3>Manage Companies</h3>
      {message && (
        <p>
          <em>{message}</em>
        </p>
      )}

      {editingCompany || isCreating ? (
        <EditForm
          company={editingCompany || {}}
          onCancel={() => {
            setEditingCompany(null);
            setIsCreating(false);
          }}
        />
      ) : (
        <button onClick={() => setIsCreating(true)}>Add New Company</button>
      )}

      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}
      >
        <thead style={{ background: '#f2f2f2' }}>
          <tr>
            <th
              style={{
                padding: '12px 8px',
                textAlign: 'left',
                borderBottom: '2px solid #EEEBF9',
              }}
            >
              Company Name
            </th>
            <th
              style={{
                padding: '12px 8px',
                textAlign: 'left',
                borderBottom: '2px solid #EEEBF9',
              }}
            >
              SFTP Folder
            </th>
            <th
              style={{
                padding: '12px 8px',
                textAlign: 'left',
                borderBottom: '2px solid #EEEBF9',
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c: CompanyProfile) => (
            <tr key={c.id} style={{ borderBottom: '1px solid #EEEBF9' }}>
              <td style={{ padding: '12px 8px' }}>{c.name}</td>
              <td style={{ padding: '12px 8px' }}>{c.baseSftpFolder}</td>
              <td style={{ padding: '12px 8px', display: 'flex', gap: '15px' }}>
                <span
                  onClick={() => setEditingCompany(c)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
                <span
                  onClick={() => handleDelete(c.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <DeleteIcon />
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
